// src/components/okrs/EditOKRDialog.jsx
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Plus, Trash2, Edit } from "lucide-react";

export default function EditOKRDialog({ open, onClose, onSubmit, okr }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    quarter: "",
    year: new Date().getFullYear(),
    status: "active",
    progress_percentage: 0,
    key_results: []
  });

  const [keyResult, setKeyResult] = useState("");

  // Initialize form data when okr prop changes
  useEffect(() => {
    if (okr) {
      setFormData({
        title: okr.title || "",
        description: okr.description || "",
        quarter: okr.quarter || "",
        year: okr.year || new Date().getFullYear(),
        status: okr.status || "active",
        progress_percentage: okr.progress_percentage || 0,
        key_results: Array.isArray(okr.key_results) ? okr.key_results : []
      });
    }
  }, [okr]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert("Please enter an objective title");
      return;
    }

    if (formData.key_results.length === 0) {
      alert("Please add at least one key result");
      return;
    }

    try {
      await onSubmit(formData);
      setKeyResult("");
    } catch (error) {
      console.error("Error updating OKR:", error);
    }
  };

  const addKeyResult = () => {
    if (!keyResult.trim()) return;

    setFormData(prev => ({
      ...prev,
      key_results: [...prev.key_results, { title: keyResult.trim() }]
    }));
    setKeyResult("");
  };

  const removeKeyResult = (index) => {
    setFormData(prev => ({
      ...prev,
      key_results: prev.key_results.filter((_, i) => i !== index)
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name === "keyResult") {
      e.preventDefault();
      addKeyResult();
    }
  };

  if (!okr) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: "600px" }}>
        <DialogHeader>
          <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 18, fontWeight: 800 }}>
            <Edit size={20} color="var(--accent)" />
            Edit OKR
          </DialogTitle>
          <p style={{ margin: 0, color: "var(--fg-muted)", fontSize: 14 }}>
            Update this objective and its key results
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 16 }}>
          {/* Objective Title */}
          <div>
            <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
              Objective Title *
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Increase user engagement and retention"
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
              Description
            </Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Provide additional context or rationale for this objective..."
              rows={3}
            />
          </div>

          {/* Quarter and Year */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
                Quarter
              </Label>
              <Select
                value={formData.quarter}
                onValueChange={(value) => setFormData(prev => ({ ...prev, quarter: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1</SelectItem>
                  <SelectItem value="Q2">Q2</SelectItem>
                  <SelectItem value="Q3">Q3</SelectItem>
                  <SelectItem value="Q4">Q4</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
                Year
              </Label>
              <Input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                min="2020"
                max="2030"
              />
            </div>
          </div>

          {/* Status and Progress */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
                Status
              </Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
                Progress (%)
              </Label>
              <Input
                type="number"
                value={formData.progress_percentage}
                onChange={(e) => setFormData(prev => ({ ...prev, progress_percentage: parseInt(e.target.value) || 0 }))}
                min="0"
                max="100"
              />
            </div>
          </div>

          {/* Key Results */}
          <div>
            <Label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
              Key Results *
            </Label>

            {/* Add new key result */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <Input
                name="keyResult"
                value={keyResult}
                onChange={(e) => setKeyResult(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Increase monthly active users to 10,000"
                style={{ flex: 1 }}
              />
              <Button type="button" onClick={addKeyResult} disabled={!keyResult.trim()}>
                <Plus size={16} />
              </Button>
            </div>

            {/* List of key results */}
            {formData.key_results.length > 0 && (
              <div style={{ display: "grid", gap: 8 }}>
                {formData.key_results.map((kr, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 12,
                      background: "var(--bg-inset)",
                      borderRadius: 8,
                      border: "1px solid var(--border)"
                    }}
                  >
                    <span style={{ fontSize: 14, color: "var(--fg-secondary)" }}>
                      {kr.title || kr}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeKeyResult(index)}
                      style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              Update OKR
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}